<script setup lang="ts">
import { T } from '@tolgee/vue'
import { computed, ref, watch } from 'vue'
import { type FortuneWheelPrize } from '@/types/shared/fortune-wheel'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { useWatchOverlays } from '@/composables/helpers/scoll'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'

const props = defineProps<{
  show: boolean
  prize: FortuneWheelPrize
}>()

defineEmits<{
  (e: 'hide'): void
  (e: 'onCheckEntries'): void
}>()

useWatchOverlays(computed(() => props.show))
const { topStyle } = useFullScreenDimensions()

const isClickable = ref(false)

watch(() => props.show, show => {
  if (show) {
    setTimeout(() => isClickable.value = true, 500)
  } else {
    isClickable.value = false
  }
}, { immediate: true })

</script>
<template>
  <Transition
    name="fade"
    mode="out-in"
  >
    <div
      v-if="show"
      class="fixed h-screen max-h-dvh w-screen overflow-hidden left-0 bottom-0 bg-dark-most-bottom-layer/[.95] flex flex-col z-[800]"
      @click="$emit('hide')"
    >
      <CrossCircleIcon
        class="w-6 h-6 min-w-[24px] absolute top-3 right-3 text-white/[87%]"
        :style="topStyle"
      />
      <div class="h-full w-full flex-1 flex flex-col gap-y-6 items-center justify-center relative">
        <div
          class="absolute w-[334px] h-[334px] rounded-full bg-green-primary blur-[150px] -z-[1]"
        />
        <div class="flex flex-col items-center gap-y-3">
          <h1 class="text-white text-[36px] font-bold leading-[42px] text-center max-w-[316px]">
            <T
              key-name="fortune_wheel.ticket_overlay.title"
              default-value="Congrats, you got 1 ticket!"
            />
          </h1>
          <p class="text-base text-light-white font-medium -tracking-[0.32px] whitespace-pre-line text-center">
            <T
              key-name="fortune_wheel.ticket_overlay.description"
              :default-value="`You’re in the draw for the Lucky Rush.\nGood luck!`"
            />
          </p>
        </div>

        <img
          src="@/assets/images/gifs/ticket.gif"
          class="size-[160px]"
        >
        <div
          class="mt-6 flex items-center flex-col gap-y-3"
        >
          <div @click.stop.prevent="$emit('onCheckEntries')">
            <BaseButton
              class="min-w-[286px]"
            >
              <T
                key-name="fortune_wheel.ticket_overlay.cta"
                default-value="CHECK MY ENTRIES"
              />
            </BaseButton>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from, .fade-leave-to /* .fade-leave-active in <2.1.8 */
{
  opacity: 0;
}
</style>